import SvgIcon from '@home/components/SvgIcon';
import {
  addSummaryKeywrodUrlList,
  updateSummaryKeywordDrillUserInfo,
  updateSummaryUrlDrill,
} from '@op/services';
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Input, Button, Icon, notification } from 'tea-component';
import { useIntl, useParams } from 'umi';
import './index.scss';
import IconSaveSuccess from '@op/assets/svg/icon-save-success.svg';
import SaveFloatButton from '../SaveFloatButton';
import useLanguage from '@op/hooks/useLanguage';

export default function SaveAnalysisModal({
  visible,
  close,
  wholeGameInfo,
  saveUserSummary,
  getSaveWindowsName,
}: {
  visible: boolean;
  close?: () => void;
  wholeGameInfo: any;
  saveUserSummary: (name: string) => Promise<boolean>;
  getSaveWindowsName: () => string;
}) {
  const { formatMessage } = useIntl();
  const [name, setName] = useState('');
  const isError = useMemo(() => name.trim() === '', [name]);

  useEffect(() => {
    if (visible)
      setName(`${wholeGameInfo.entity_name}_${getSaveWindowsName()}`);
  }, [visible, getSaveWindowsName]);

  async function submit() {
    const result = await saveUserSummary(name);

    if (result) close?.();
  }

  return (
    <Modal
      visible={visible}
      caption={formatMessage({ id: 'Save to Analysis List' })}
      onClose={close}
      className="dbo-save-analysis-modal"
    >
      <Modal.Body>
        <p>
          {formatMessage({
            id: 'The analysis will take 15 to 30 minutes to generate.',
          })}
        </p>
        <p>
          {formatMessage(
            {
              id: 'You can view the analysis in the “{text}”.',
            },
            {
              text: <b>{formatMessage({ id: 'Saved Analysis List' })}</b>,
            },
          )}
        </p>
        <Form>
          <Form.Item label={formatMessage({ id: 'Analysis Name' })}>
            <div className="dbo-save-analysis-modal-input">
              <Input
                value={name}
                onChange={(value: string) => setName(value)}
                placeholder={formatMessage({
                  id: 'Please enter a report name',
                })}
              />
              {name && (
                <Icon
                  type="dismiss"
                  onClick={() => setName('')}
                  className="dbo-clear-btn"
                ></Icon>
              )}
            </div>
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" disabled={isError} onClick={submit}>
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={close}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * 基础分析保存 Hook
 * 提供通用的状态和UI逻辑
 */
function useBaseAnalysisSave({
  isPreviews,
  changeTab,
  gptLoading,
}: {
  isPreviews: boolean;
  changeTab?: () => void;
  gptLoading: boolean;
}) {
  const { id } = useParams<{ id: string }>();
  const { formatMessage } = useIntl();
  const [saveVisible, setSaveVisible] = useState<boolean>(false);
  const [saveParams, setSaveParams] = useState<any>({});
  const [isSave, setIsSave] = useState(false);
  const [saveId, setSaveId] = useState<number | undefined>(
    isPreviews && id ? Number(id) : undefined,
  );
  const haveSaveId = useMemo(() => saveId !== undefined, [saveId]);

  function resetSaveId() {
    setSaveId(undefined);
  }

  function toggleSaveWindow() {
    setSaveVisible((flag) => !flag);
  }

  const { saveTipsElement, saveTipsIcon } = useMemo(() => {
    if (isPreviews || gptLoading)
      return {
        saveTipsElement: null,
        saveTipsIcon: null,
      };
    if (isSave) {
      const text = formatMessage(
        { id: 'This analysis report has been saved to the {page}' },
        {
          page: (
            <Button
              style={{ marginLeft: 5, width: 'auto' }}
              type="link"
              onClick={() => changeTab?.()}
            >
              {formatMessage({ id: '[Summary List]' })}
            </Button>
          ),
        },
      );

      const element = (
        <div className="dbo-save-tips">
          <span className="dbo-save-tips-icon">
            <img src={IconSaveSuccess} />
          </span>
          {text}
        </div>
      );
      return {
        saveTipsElement: element,
        saveTipsIcon: (
          <SaveFloatButton
            success={true}
            successText={text}
            failText={undefined}
          ></SaveFloatButton>
        ),
      };
    }

    const text = formatMessage({ id: 'Save to Analysis list and view later' });
    return {
      saveTipsElement: (
        <Button
          type="link"
          className="save-analysis"
          onClick={() => toggleSaveWindow()}
        >
          <SvgIcon type="datalab-save" className="save-icon" />
          {text}
        </Button>
      ),
      saveTipsIcon: (
        <SaveFloatButton
          success={false}
          successText={undefined}
          failText={text}
          handleClick={toggleSaveWindow}
        ></SaveFloatButton>
      ),
    };
  }, [haveSaveId, formatMessage, isPreviews, gptLoading, isSave]);

  return {
    saveVisible,
    saveParams,
    setSaveParams,
    resetSaveId,
    setSaveId,
    saveTipsElement,
    saveTipsIcon,
    toggleSaveWindow,
    haveSaveId,
    setIsSave,
    saveId,
  };
}

/**
 * Keyword 分析保存 Hook
 * 专门处理 Keyword 场景下的保存和更新逻辑
 */
export function useKeywordAnalysisSave({
  isPreviews,
  gameInfo,
  changeTab,
  isCreator,
}: {
  isCreator: boolean;
  isPreviews: boolean;
  gameInfo: any;
  changeTab?: () => void;
}) {
  const { formatMessage } = useLanguage();
  const [keywordGptLoading, setKeywordGptLoading] = useState<boolean>(false);

  const { setSaveId, saveParams, haveSaveId, saveId, setIsSave, ...baseHooks } =
    useBaseAnalysisSave({
      isPreviews,
      changeTab,
      gptLoading: keywordGptLoading,
    });

  async function saveUserSummary(
    name: string,
    otherParams = {},
  ): Promise<boolean> {
    // 没有名称就不保存
    if (!name) return false;

    try {
      const { id, summary_task_id, ...info } = saveParams;
      await addSummaryKeywrodUrlList({
        name,
        lead_id: id,
        ...info,
        ...otherParams,
      });
      setIsSave(true);
      notification.success({
        description: formatMessage({ id: 'Successfully saved analysis' }),
      });
      return true;
    } catch (error) {
      notification.error({
        description: formatMessage({ id: 'Analysis save failed' }),
      });
      return false;
    }
  }

  function updateKeywordSummary(data: {
    id: number;
    summary_task_id: string;
    short_url: string;
  }) {
    // 没有saveId无法保存
    if (!haveSaveId) return false;
    // 如果不是本人，不进行更改，但是告诉已经更改
    if (!isCreator) return true;
    // 现在keyword不需要Id，id由gpt直接传过来
    if (!data?.id) return false;
    return updateSummaryKeywordDrillUserInfo({
      ...gameInfo,
      ...data,
    })
      .then(() => {
        notification.success({
          description: formatMessage({ id: 'Prompt edit success' }),
        });
        return true;
      })
      .catch(() => {
        notification.error({
          description: formatMessage({ id: 'Prompt edit failed' }),
        });
        return false;
      });
  }

  return {
    ...baseHooks,
    saveUserSummary,
    updateKeywordSummary,
    keywordGptLoading,
    setKeywordGptLoading,
    setSaveId,
    setIsSave,
  };
}

/**
 * Url 分析保存 Hook
 * 专门处理 Topic（URL）场景下的保存和更新逻辑
 */
export function useUrlAnalysisSave({
  isPreviews,
  isCreator,
  changeTab,
}: {
  isCreator: boolean;
  isPreviews: boolean;
  changeTab?: () => void;
}) {
  const { formatMessage } = useLanguage();
  const [topicGptLoading, setTopicGptLoading] = useState<boolean>(false);
  const [updateParams, setUpdateParams] = useState<any>({});

  const { haveSaveId, saveParams, setSaveId, setIsSave, ...baseHooks } =
    useBaseAnalysisSave({
      isPreviews,
      changeTab,
      gptLoading: topicGptLoading,
    });

  async function saveUserSummary(name: string, otherParams = {}) {
    // 没有名称就不保存
    if (!name || haveSaveId) return false;

    try {
      const data = await addSummaryKeywrodUrlList({
        ...saveParams,
        name,
        ...otherParams,
      });
      setSaveId(data.id);
      setIsSave(true);
      notification.success({
        description: formatMessage({ id: 'Successfully saved analysis' }),
      });
      return true;
    } catch (error) {
      notification.error({
        description: formatMessage({ id: 'Analysis save failed' }),
      });
      return false;
    }
  }

  function updateUrlSummary(data: any) {
    // 没有Id无法保存
    if (!updateParams.lead_id) return false;
    // 如果不是本人，不进行更改，但是告诉已经更改
    if (!isCreator) return true;

    return updateSummaryUrlDrill({
      lead_id: updateParams.lead_id,
      ...data,
    })
      .then(() => {
        notification.success({
          description: formatMessage({ id: 'Prompt edit success' }),
        });
        return true;
      })
      .catch(() => {
        notification.error({
          description: formatMessage({ id: 'Prompt edit failed' }),
        });

        return false;
      });
  }

  return {
    ...baseHooks,
    saveUserSummary,
    updateUrlSummary,
    setUpdateParams,
    topicGptLoading,
    setTopicGptLoading,
    setSaveId,
    setIsSave,
  };
}
